/**
 * 성취기준 선택기 — 헤비유저용 localStorage 기억 (docs/achievement_standards_ux.md D4·D6)
 *
 *   - 교육과정 기억: 교사가 명시적으로 바꾼 개정을 학교급·학년도별로 기억한다.
 *   - 최근 과목: 성취기준을 넣은 과목을 학교급·교육과정별로 기억한다(최대 12, 화면에는 4).
 *   - 최근 성취기준: 넣은 성취기준을 기억한다(최대 40, 화면에는 6).
 *
 * 저장하는 것은 코드·key·과목명·라벨뿐이다(학생 정보 없음). 프라이빗 모드·용량 초과·손상된 JSON은
 * 모두 조용히 넘어가고 빈 값을 돌려준다 — 기억은 편의 기능이라 흐름을 막지 않는다.
 */

import { recordIdentity, toRecordSummary } from './achievementStandardPicker.utils';

export const RECENTS_STORAGE_KEYS = {
  curriculum: 'asp:curriculum:v1',
  special: 'asp:special:v1',
  standards: 'asp:recent-standards:v1',
  subjects: 'asp:recent-subjects:v1',
};

export const RECENT_SUBJECTS_LIMIT = 4;
export const RECENT_STANDARDS_LIMIT = 6;
const STORED_SUBJECTS_CAP = 12;
const STORED_STANDARDS_CAP = 40;

const hasStorage = () => {
  try {
    return typeof localStorage !== 'undefined' && localStorage !== null;
  } catch {
    return false;
  }
};

const readJson = (key, fallback) => {
  if (!hasStorage()) return fallback;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return parsed === null || parsed === undefined ? fallback : parsed;
  } catch {
    return fallback;
  }
};

const writeJson = (key, value) => {
  if (!hasStorage()) return;
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // 용량 초과·프라이빗 모드: 기억은 최선 노력
  }
};

const asArray = (value) => (Array.isArray(value) ? value : []);
const asObject = (value) => (value && typeof value === 'object' && !Array.isArray(value) ? value : {});

// ---------------------------------------------------------------------------
// 교육과정 기억 (D4)
// ---------------------------------------------------------------------------

export const readCurriculumPreference = ({ levelId, schoolYear } = {}) => {
  if (!levelId) return null;
  const entry = asObject(readJson(RECENTS_STORAGE_KEYS.curriculum, {}))[levelId];
  if (!entry || typeof entry !== 'object') return null;
  if (schoolYear != null && Number(entry.schoolYear) !== Number(schoolYear)) return null;
  return entry.curriculum === '2015' || entry.curriculum === '2022' ? entry.curriculum : null;
};

export const rememberCurriculumPreference = ({ curriculum, levelId, schoolYear } = {}) => {
  if (!levelId || (curriculum !== '2015' && curriculum !== '2022')) return;
  const current = asObject(readJson(RECENTS_STORAGE_KEYS.curriculum, {}));
  writeJson(RECENTS_STORAGE_KEYS.curriculum, {
    ...current,
    [levelId]: { curriculum, schoolYear: schoolYear == null ? null : Number(schoolYear) },
  });
};

/** 특수교육 기본 교육과정 모드. 특수학교·특수학급 교사는 계속 쓰므로 학교급·학년도와 상관없이 기억한다. */
export const readSpecialPreference = () => readJson(RECENTS_STORAGE_KEYS.special, false) === true;

export const rememberSpecialPreference = (enabled) => {
  writeJson(RECENTS_STORAGE_KEYS.special, Boolean(enabled));
};

// ---------------------------------------------------------------------------
// 최근 과목 (D6)
// ---------------------------------------------------------------------------

const subjectScope = (entry, { curriculum, levelId }) => (
  entry?.levelId === levelId && entry?.curriculum === curriculum
);

export const readRecentSubjects = ({ curriculum, levelId, limit = RECENT_SUBJECTS_LIMIT } = {}) => (
  asArray(readJson(RECENTS_STORAGE_KEYS.subjects, []))
    .filter((entry) => entry?.id && entry?.label && subjectScope(entry, { curriculum, levelId }))
    .slice(0, limit)
);

export const rememberRecentSubject = ({ curriculum, levelId, subject } = {}) => {
  if (!subject?.id || !levelId || !curriculum) return;
  const entry = {
    curriculum,
    gradeBand: subject.grade_band || subject.gradeBand || null,
    groupId: subject.groupId || null,
    id: subject.id,
    label: subject.label || subject.id,
    levelId,
  };
  const rest = asArray(readJson(RECENTS_STORAGE_KEYS.subjects, []))
    .filter((item) => !(item?.id === entry.id && item?.curriculum === curriculum && item?.levelId === levelId));
  writeJson(RECENTS_STORAGE_KEYS.subjects, [entry, ...rest].slice(0, STORED_SUBJECTS_CAP));
};

// ---------------------------------------------------------------------------
// 최근 성취기준 (D6)
// ---------------------------------------------------------------------------

export const readRecentStandards = ({
  curriculum,
  levelId,
  limit = RECENT_STANDARDS_LIMIT,
  subjectId,
} = {}) => (
  asArray(readJson(RECENTS_STORAGE_KEYS.standards, []))
    .filter((entry) => entry?.record?.key && subjectScope(entry, { curriculum, levelId }))
    .filter((entry) => !subjectId || entry.record.subject_id === subjectId)
    .map((entry) => entry.record)
    .slice(0, limit)
);

export const rememberRecentStandards = ({ levelId, records } = {}) => {
  const incoming = asArray(records).filter((record) => record?.key);
  if (incoming.length === 0 || !levelId) return;
  const summaries = incoming.map((record) => toRecordSummary(record));
  const incomingIds = new Set(summaries.map(recordIdentity));
  const rest = asArray(readJson(RECENTS_STORAGE_KEYS.standards, []))
    .filter((entry) => entry?.record?.key && !incomingIds.has(recordIdentity(entry.record)));
  const next = summaries.map((record) => ({ curriculum: record.curriculum, levelId, record }));
  writeJson(RECENTS_STORAGE_KEYS.standards, [...next, ...rest].slice(0, STORED_STANDARDS_CAP));
};
